import { Injectable, Inject } from '@angular/core';

import { Store } from '@ngrx/store';

import { UUID } from 'angular2-uuid';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { AlertOptions, AlertState, AlertType, AlertDisplayMode } from '../notification.models';
import { NotificationAction } from './notification.actions';
import * as NotificationSelector from './notification.selectors';
import { NotificationPartialStoreState } from './notification.reducer';
import Env from '@ridango/core/env';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class NotificationFacade {
    private alerts$: Observable<AlertState[]>;

    constructor(
        private store: Store<NotificationPartialStoreState>,
        private actions: NotificationAction,
        private translate: TranslateService,
        @Inject('ENV') private env: Env
    ) {
        this.alerts$ = this.store.select(NotificationSelector.getAlerts);
    }

    /**
     * Returns an observable of all alerts from the store
     */
    getAlerts(): Observable<AlertState[]> {
        return this.alerts$;
    }

    /**
     * Returns an observable of alerts filtered by display mode
     */
    getAlertsByMode(mode: AlertDisplayMode): Observable<AlertState[]> {
        return this.alerts$.pipe(
            map((alerts) =>
                alerts.filter((alert) => {
                    const alertMode = alert.displayMode || this.getDefaultDisplayMode();
                    return alertMode === mode;
                })
            )
        );
    }

    /**
     * Returns inline alerts (default for web/desktop)
     */
    onAlert(): Observable<AlertState[]> {
        // Only return inline alerts that are NOT targeted to a specific component
        return this.getAlertsByMode(AlertDisplayMode.Inline).pipe(
            map((alerts) => alerts.filter((alert) => !alert.target))
        );
    }

    /**
     * Returns modal/popup alerts (default for mobile)
     */
    onAlertV2(): Observable<AlertState[]> {
        // Only return modal alerts that are NOT targeted to a specific component
        return this.getAlertsByMode(AlertDisplayMode.Modal).pipe(
            map((alerts) => alerts.filter((alert) => !alert.target))
        );
    }

    /**
     * Returns alerts filtered by target identifier.
     * @param targetId The target identifier (e.g., 'page-header', 'form-footer')
     * @returns Observable of alerts matching the target
     */
    getAlertsByTarget(targetId: string): Observable<AlertState[]> {
        return this.alerts$.pipe(map((alerts) => alerts.filter((alert) => alert.target === targetId)));
    }

    /**
     * Returns alerts filtered by both target identifier and display mode.
     * Useful for specific component instances that have specific display requirements.
     * @param targetId The target identifier (e.g., 'payment-modal')
     * @param mode The display mode to filter by
     * @returns Observable of alerts matching both target and mode
     */
    getAlertsByTargetAndMode(targetId: string, mode: AlertDisplayMode): Observable<AlertState[]> {
        return this.alerts$.pipe(
            map((alerts) =>
                alerts.filter(
                    (alert) => alert.target === targetId && (alert.displayMode || this.getDefaultDisplayMode()) === mode
                )
            )
        );
    }

    /**
     * Determine default display mode based on platform
     */
    private getDefaultDisplayMode(): AlertDisplayMode {
        return this.env.IS_MOBILE_APP ? AlertDisplayMode.Modal : AlertDisplayMode.Inline;
    }

    /**
     * Normalize a user-provided displayMode which may be a string or enum into the enum.
     * Accepts 'inline'|'modal' (case-insensitive) or AlertDisplayMode values.
     */
    private resolveDisplayMode(input?: AlertDisplayMode | string): AlertDisplayMode {
        if (input === undefined || input === null) return this.getDefaultDisplayMode();
        if (typeof input === 'string') {
            const lower = input.toLowerCase();
            if (lower === 'inline') return AlertDisplayMode.Inline;
            if (lower === 'modal') return AlertDisplayMode.Modal;
        }
        // If already an AlertDisplayMode value, just return it
        return input as AlertDisplayMode;
    }

    private resolveTarget(options?: AlertOptions, displayMode?: AlertDisplayMode): AlertOptions | undefined {
        // For mobile apps, default modal alerts should go to 'global-modal' target
        if (this.env.IS_MOBILE_APP && displayMode === AlertDisplayMode.Modal && !options?.target) {
            return { ...options, target: 'global-modal' };
        }
        return options;
    }

    private alert(type: AlertType, message: string, title: string, options?: AlertOptions) {
        // Resolve displayMode: explicit or env-based default
        const displayMode = this.resolveDisplayMode(options?.displayMode as AlertDisplayMode | string);
        options = this.resolveTarget(options, displayMode);
        const alertConfig: AlertState = {
            id: UUID.UUID(),
            type,
            message,
            title,
            ...options,
            displayMode
        };
        // Dispatch asynchronously to avoid triggering ExpressionChangedAfterItHasBeenCheckedError
        // when alerts are created during Angular change detection (e.g., in ngOnInit).
        Promise.resolve().then(() => this.store.dispatch(this.actions.addAlert(alertConfig)));
    }

    success(message: string, options?: AlertOptions) {
        const title = options?.title || this.translate.instant('success');
        this.alert(AlertType.Success, message, title, options);
    }

    error(message: string, options?: AlertOptions) {
        const title = options?.title || this.translate.instant('error');
        this.alert(AlertType.Error, message, title, options);
    }

    info(message: string, options?: AlertOptions) {
        const title = options?.title || this.translate.instant('information');
        this.alert(AlertType.Info, message, title, options);
    }

    warn(message: string, options?: AlertOptions) {
        const title = options?.title || this.translate.instant('warning');
        this.alert(AlertType.Warning, message, title, options);
    }

    prepareNext() {
        this.store.dispatch(this.actions.filterNextPageAlerts());
    }

    remove(alert: AlertState) {
        this.store.dispatch(this.actions.removeAlert(alert));
    }

    /**
     * Remove all alerts targeted to a specific component instance.
     * Useful for cleaning up modal-specific alerts when a modal closes.
     * @param targetId The target identifier (e.g., 'payment-modal')
     */
    removeAlertsByTarget(targetId: string) {
        this.store
            .select(NotificationSelector.getAlerts)
            .pipe(take(1))
            .subscribe((alerts) => {
                alerts.forEach((alert) => {
                    if (alert.target === targetId) {
                        this.remove(alert);
                    }
                });
            });
    }

    removeAllExceptNext() {
        this.store.dispatch(this.actions.clearRouteChangeFlags());
    }

    removeAll() {
        this.store.dispatch(this.actions.removeAll());
    }

    /**
     * Legacy V2 methods for backward compatibility
     * These show alerts with Modal display mode (for mobile/popup style)
     */
    successV2(message: string, options?: AlertOptions) {
        const title = options?.title || this.translate.instant('success');
        this.alert(AlertType.Success, message, title, { ...options, displayMode: AlertDisplayMode.Modal });
    }

    infoV2(message: string, options?: AlertOptions) {
        const title = options?.title || this.translate.instant('information');
        this.alert(AlertType.Info, message, title, { ...options, displayMode: AlertDisplayMode.Modal });
    }

    errorV2(message: string, options?: AlertOptions) {
        const title = options?.title || this.translate.instant('error');
        this.alert(AlertType.Error, message, title, { ...options, displayMode: AlertDisplayMode.Modal });
    }

    removeAllAlertsV2(): void {
        this.removeAll();
    }
}
